import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAppContext } from "@app/AppContext";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import {
  DataDialog,
  DataPagination,
  DataTable,
  DatePickerInput,
} from "@app/components/atoms";
import {
  IconLocationMachine,
  IconUser,
  IconInvalidUrl,
  IconInputError,
} from "@app/components/icons";
import {
  Box,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Menu,
  MenuItem,
  Checkbox,
  Typography,
  TextField,
} from "@mui/material";
import Add from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { PictureAsPdfOutlined } from "@mui/icons-material";

import {
  Machine,
  screenPermission,
  pageInfo,
  MachineInfoType,
} from "@app/types";
import {
  INVALID_URL,
  ReportType,
  ResponseStatus,
  CURRENT_PAGE,
  TOTAL_PAGE,
  LIMIT_PAGE,
  DATE_TIME_FORMAT_2,
  ReportTypeDialog,
  MAINTENANCE_REPORTS,
  INCIDENT_REPORTS,
  INSPECTION,
  FUEL_MAINTENANCE_REPORTS,
  MaintenanceReportType,
} from "@app/constants";
import {
  apiGetPeriodicMaintenanceAvailability,
  apiGroupDetail,
  getBulkReportsPdf,
  getMachineInfo,
  getMachineReportInfo,
  getMachineSummary,
  getReportsMediaCount,
  getReportsPrintLimit,
  getUser,
  groupsMachinesReportsCSV,
} from "@app/api";
import {
  isMobile,
  scrollToTop,
  resetIsContinuousLoading,
  formatNumberWithCommas,
  getCurrentUtc,
  clearMachineReportsPreferences,
  REGEX,
} from "@app/helpers";
import {
  RootState,
  saveScreenPermission,
  setShowAlert,
  store,
} from "@app/store";
import { useDispatch, useSelector } from "react-redux";
import { InputText } from "primereact/inputtext";
import "@app/pages/Features/GroupList/GroupDetail/MachineTab/MachineTab.css";
import "@app/pages/Features/MachineDetail/Components/MachineInfo/MachineInfo.css";
import { DatePickerExcelInput } from "@app/components/atoms/DatePickerInputExcel/DatePickerInputExcel";
import { AllReportsDataDialog } from "@app/pages/Features/GroupList/GroupDetail/MachineReportsHistoryTab/AllReportsDataDialog";
import { InspectionReportItemsDialog } from "@app/pages/Features/GroupList/GroupDetail/MachineReportsHistoryTab/InspectionReportItemsDialog";
import {
  FuelMaintenanceReportIcon,
  IncidentReportIcon,
  InspectionIconMobile,
  MaintenanceReportIcon,
} from "@app/components/icons/ReportIcons/ReportIcons";
import { CreateInspections } from "./CreateReports";
import { CreateAbnormality } from "./CreateReports/Abnormality/CreateAbnormality";
import { CreateRefuel } from "./CreateReports/Refuel/CreateRefuel";
import { CreateMaintenance } from "./CreateReports/Maintenance/CreateMaintenance";
import { MaintenanceDraftsListDialog } from "./CreateReports/Maintenance/MaintenanceDraftsListDialog";
import { BulkPdfSelectionBar } from "./BulkPdfSelectionBar";
import { apiGetMaintenanceReportDraftList } from "@app/api/machines/machines.api";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
// BulkPdfExportDialog kept in project for future use
// import { BulkPdfExportDialog } from "./BulkPdfExportDialog";
// import type { BulkPdfExportFormat } from "./BulkPdfExportDialog";

const sanitizeBulkExportFileNameDateTime = (value: string) =>
  value.replace(/[\\/:*?"<>|\s]/g, "");
type SummaryFilterType = {
  label: string;
  filterCondition: string;
  count: number;
  key: string;
  icon?: JSX.Element;
};

type ReportCreationType = SummaryFilterType & {
  maintenanceType?: MaintenanceReportType;
};

type MachineSummaryType = {
  reportCount: number;
  machineOperationReportCount: number;
  fuelMaintenanceReportCount: number;
  incidentReportCount: number;
  inspectionReportCount: number;
};

type SelectedReportMeta = {
  type: string;
  reportedAt: string;
};

const dataCountSummaryFilter: SummaryFilterType[] = [
  {
    label: "all",
    filterCondition: "",
    count: 0,
    key: "reportCount",
  },
  {
    label: "labelInspection",
    filterCondition: INSPECTION,
    count: 0,
    key: "inspectionReportCount",
    icon: <InspectionIconMobile />,
  },
  {
    label: "abnormalityOrIssue",
    filterCondition: INCIDENT_REPORTS,
    count: 0,
    key: "incidentReportCount",
    icon: <IncidentReportIcon />,
  },
  {
    label: "refuel",
    filterCondition: FUEL_MAINTENANCE_REPORTS,
    count: 0,
    key: "fuelMaintenanceReportCount",
    icon: <FuelMaintenanceReportIcon />,
  },
  {
    label: "maintenanceReport",
    filterCondition: MAINTENANCE_REPORTS,
    count: 0,
    key: "maintenanceReportCount",
    icon: <MaintenanceReportIcon />,
  },
];

const reportCreationOptions: ReportCreationType[] = [
  ...dataCountSummaryFilter.slice(1, -1),
];

const MENU_ITEM_HEIGHT_PX = 46;

const maintenanceSubMenuOptions: ReportCreationType[] = [
  {
    label: "dialogMaintenanceReports.isPeriodicMaintenance",
    filterCondition: MAINTENANCE_REPORTS,
    count: 0,
    key: "maintenanceReportCount",
    icon: <RefreshRoundedIcon className="icon-rotate lighten" />,
    maintenanceType: MaintenanceReportType.PERIODIC_MAINTENANCE,
  },
  {
    label: "generalMaintenance",
    filterCondition: MAINTENANCE_REPORTS,
    count: 0,
    key: "maintenanceReportCount",
    icon: <Add className="lighten" />,
    maintenanceType: MaintenanceReportType.GENERAL_MAINTENANCE,
  },
];

function hasScreenPermissionsLoaded(
  permissions: screenPermission | Record<string, never>,
): boolean {
  return (
    Object.keys(permissions).length > 0 &&
    typeof permissions.allowCreateInspectionAndReport === "boolean"
  );
}

export function Report() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [dataCell, setDataCell] = useState<Machine>();
  const [dataSource, setDataSource] = useState<Machine[]>();
  const [open, setOpen] = useState(false);
  const { groupId, machineId } = useParams();
  const [field, setField] = useState("");
  const [isInit, setIsInit] = useState(true);
  const [openExportReports, setExportReports] = useState(false);
  const [openBulkPdfConfirmDialog, setOpenBulkPdfConfirmDialog] =
    useState(false);
  const [openBulkPdfMediaLimitDialog, setOpenBulkPdfMediaLimitDialog] =
    useState(false);
  const [openBulkPdfRemarksDialog, setOpenBulkPdfRemarksDialog] =
    useState(false);
  const [isBulkPdfExporting, setIsBulkPdfExporting] = useState(false);
  const [isBulkPdfSelectionMode, setIsBulkPdfSelectionMode] = useState(false);
  const [isCheckingBulkPdfMediaCount, setIsCheckingBulkPdfMediaCount] =
    useState(false);
  const [isBulkPdfMediaLimitExceeded, setIsBulkPdfMediaLimitExceeded] =
    useState(false);
  const [bulkPdfMediaCountInfo, setBulkPdfMediaCountInfo] = useState<{
    mediaCount: number;
    maxMediaCount: number;
  } | null>(null);
  const [pdfHeaderRemarks, setPdfHeaderRemarks] = useState("");
  const [pdfFooterRemarks, setPdfFooterRemarks] = useState("");
  const [pdfHeaderRemarksError, setPdfHeaderRemarksError] = useState(false);
  const [pdfFooterRemarksError, setPdfFooterRemarksError] = useState(false);
  const [openCreateInspection, setOpenCreateInspection] = useState(false);
  const [openCreateAbnormality, setOpenCreateAbnormality] = useState(false);
  const [openCreateRefuel, setOpenCreateRefuel] = useState(false);
  const [openCreateMaintenance, setOpenCreateMaintenance] = useState(false);
  const [openMaintenanceDrafts, setOpenMaintenanceDrafts] = useState(false);
  const [maintenanceDraftsCount, setMaintenanceDraftsCount] = useState(0);
  const [createReportMenuAnchor, setCreateReportMenuAnchor] =
    useState<null | HTMLElement>(null);
  const isCreateReportMenuOpen = Boolean(createReportMenuAnchor);
  const [maintenanceSubMenuAnchor, setMaintenanceSubMenuAnchor] =
    useState<null | HTMLElement>(null);
  const isMaintenanceSubMenuOpen = Boolean(maintenanceSubMenuAnchor);
  const closeCreateReportMenus = useCallback(() => {
    setCreateReportMenuAnchor(null);
    setMaintenanceSubMenuAnchor(null);
  }, []);
  const { machineModelType } = useAppContext();
  const [periodicScheduleAvailable, setPeriodicScheduleAvailable] = useState<
    boolean | null
  >(null);
  const periodicAvailabilityFetchedForModelRef = useRef<string | null>(null);
  const prefetchPeriodicScheduleAvailability =
    useCallback((): Promise<void> => {
      if (!machineModelType) {
        return Promise.resolve();
      }
      if (periodicAvailabilityFetchedForModelRef.current === machineModelType) {
        return Promise.resolve();
      }
      periodicAvailabilityFetchedForModelRef.current = machineModelType;
      return apiGetPeriodicMaintenanceAvailability(machineModelType)
        .then((available: boolean) => {
          setPeriodicScheduleAvailable(available);
        })
        .catch((error: unknown) => {
          console.error(
            "Failed to load periodic maintenance availability:",
            error,
          );
          setPeriodicScheduleAvailable(true);
        });
    }, [machineModelType]);
  // const createReportMenuOptions = dataCountSummaryFilter.filter(
  //   (tab) => tab.filterCondition && tab.filterCondition !== INSPECTION,
  // );
  const createReportMenuOptions = reportCreationOptions.filter(
    (tab) => tab.filterCondition,
  );
  const [order, setOrder] = useState<string | undefined>("");
  const [isLoading, setIsLoading] = useState(false);
  const [pageInfo, setPageInfo] = useState({
    total: TOTAL_PAGE,
    page: CURRENT_PAGE,
    limit: LIMIT_PAGE,
  });
  const { isRequestedMachineInfo } = useSelector(
    (state: RootState) => state.inspection,
  );
  const { isUpdatedMachineReportResponses } = useSelector(
    (state: RootState) => state.inspection,
  );
  const screenPermission = useSelector(
    (state: RootState) => state.screenPermission.value,
  );
  const screenPermissionsLoaded = hasScreenPermissionsLoaded(screenPermission);
  const canCreateInspectionAndReport =
    screenPermission?.allowCreateInspectionAndReport === true;
  const [filterCondition, setFilterCondition] = useState<ReportTypeDialog | "">(
    "",
  );
  const [maintenanceType, setMaintenanceType] = useState<
    MaintenanceReportType | ""
  >("");

  useEffect(() => {
    if (!groupId || screenPermissionsLoaded) {
      return;
    }

    apiGroupDetail(groupId)
      .then((response) => {
        const permissions = response?.data?.meta?.screenPermission;
        if (permissions) {
          dispatch(saveScreenPermission(permissions));
        }
      })
      .catch((error) => {
        console.error("Failed to load screen permissions:", error);
      });
  }, [groupId, dispatch, screenPermissionsLoaded]);

  const fetchMaintenanceDraftsCount = useCallback(() => {
    if (!groupId || !machineId) return;
    apiGetMaintenanceReportDraftList(groupId, machineId)
      .then((res) => {
        setMaintenanceDraftsCount(res?.data?.data?.length ?? 0);
      })
      .catch((error) => {
        console.error("Failed to load maintenance report drafts count:", error);
      });
  }, [groupId, machineId]);

  useEffect(() => {
    fetchMaintenanceDraftsCount();
  }, [fetchMaintenanceDraftsCount, isUpdatedMachineReportResponses]);

  const [valueStartDate, setValueStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  });

  const [valueEndDate, setValueEndDate] = useState(() => new Date());

  useEffect(() => {
    clearMachineReportsPreferences();
  }, []);

  const [valueStartExcelDate, setValueStartExcelDate] =
    useState(valueStartDate);
  const [valueEndExcelDate, setValueEndExcelDate] = useState(new Date());

  const isExportDisabled =
    !valueStartExcelDate ||
    !valueEndExcelDate ||
    valueEndExcelDate < valueStartExcelDate;

  const [dataCountSummaryTabList, setDataCountSummaryTabList] = useState(
    dataCountSummaryFilter,
  );

  const [userTimeFormat, setUserTimeFormat] = useState<string>("yyyy/MM/dd");

  const [userTimeFormat1, setUserTimeFormat1] =
    useState<string>("yyyy/MM/dd HH:mm");
  const [localeCode, setLocaleCode] = useState("");
  const [maxBulkPdfSelection, setMaxBulkPdfSelection] = useState<number | null>(
    null,
  );
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await getUser();
        let timeFormat = response?.data?.data?.timeFormat || "yyyy/MM/dd";
        const isoLocaleCode = response?.data?.data?.isoLocaleCode;
        setUserTimeFormat(timeFormat);

        if (!timeFormat.includes("HH") && !timeFormat.includes("mm")) {
          timeFormat += " HH:mm";
        }

        setUserTimeFormat1(timeFormat);
        setLocaleCode(isoLocaleCode);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    const fetchPrintLimit = async () => {
      try {
        const response = await getReportsPrintLimit();
        const maxCount = response?.data?.data?.maxReportPrintCount;
        if (typeof maxCount === "number" && maxCount > 0) {
          setMaxBulkPdfSelection(maxCount);
        }
      } catch (error) {
        console.error("Error fetching report print limit:", error);
      }
    };

    fetchPrintLimit();
  }, []);

  const [latestServiceMeter, setLatestServiceMeter] = useState("-");
  const [latestServiceMeterUpdatedAt, setLatestServiceMeterUpdatedAt] =
    useState("");
  const [selectedReportIds, setSelectedReportIds] = useState<string[]>([]);
  const [selectedReportMetaMap, setSelectedReportMetaMap] = useState<
    Record<string, SelectedReportMeta>
  >({});

  // Reserved for BulkPdfExportDialog (kept for future use)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const selectedReportTypes = useMemo(
    () =>
      selectedReportIds
        .map((reportId) => selectedReportMetaMap[reportId]?.type)
        .filter(Boolean),
    [selectedReportIds, selectedReportMetaMap],
  );

  const isFuelMaintenanceReport = (record: Machine) =>
    (record.reportType as string | undefined) === FUEL_MAINTENANCE_REPORTS;

  const clearSelectedReports = useCallback(() => {
    setSelectedReportIds([]);
    setSelectedReportMetaMap({});
  }, []);

  const handleEnterBulkPdfSelectionMode = useCallback(() => {
    setIsBulkPdfSelectionMode(true);
  }, []);

  const handleClearAllSelectedReports = useCallback(() => {
    clearSelectedReports();
  }, [clearSelectedReports]);

  const handleCancelBulkPdfSelection = useCallback(() => {
    clearSelectedReports();
    setIsBulkPdfSelectionMode(false);
  }, [clearSelectedReports]);

  const resetBulkPdfExportState = useCallback(() => {
    clearSelectedReports();
    setIsBulkPdfMediaLimitExceeded(false);
    setBulkPdfMediaCountInfo(null);
    setPdfHeaderRemarks("");
    setPdfFooterRemarks("");
    setPdfHeaderRemarksError(false);
    setPdfFooterRemarksError(false);
  }, [clearSelectedReports]);

  const handleBulkPdfExportDialogClose = useCallback(() => {
    if (isBulkPdfExporting) {
      return;
    }

    resetBulkPdfExportState();
    setOpenBulkPdfConfirmDialog(false);
    setOpenBulkPdfMediaLimitDialog(false);
    setOpenBulkPdfRemarksDialog(false);
  }, [isBulkPdfExporting, resetBulkPdfExportState]);

  const handleReportCheckboxChange = (record: Machine) => {
    const reportId = record.reportedId;
    if (
      !reportId ||
      isFuelMaintenanceReport(record) ||
      maxBulkPdfSelection == null
    ) {
      return;
    }

    setSelectedReportIds((prev) => {
      if (prev.includes(reportId)) {
        setSelectedReportMetaMap((prevMap) => {
          const nextMap = { ...prevMap };
          delete nextMap[reportId];
          return nextMap;
        });
        return prev.filter((id) => id !== reportId);
      }

      if (prev.length >= maxBulkPdfSelection) {
        store.dispatch(
          setShowAlert(t("bulkPdfMaxSelection", { max: maxBulkPdfSelection })),
        );
        return prev;
      }

      setSelectedReportMetaMap((prevMap) => ({
        ...prevMap,
        [reportId]: {
          type: (record.reportType as string) || "",
          reportedAt: record.reportedAt || "",
        },
      }));

      return [...prev, reportId];
    });
  };

  const handleClickLocation = (column: any) => {
    const convertColumn = {
      machineName: column?.reportTitle,
      ...column,
    };
    setDataCell(convertColumn);
    setOpen(true);
  };

  const columns = [
    {
      key: "selection",
      title: <></>,
      dataIndex: "reportedId",
      align: "center",
      sorted: false,
      forceRender: true,
      style: {
        minWidth: 56,
        width: 56,
        paddingLeft: 4,
        paddingRight: 4,
      },
      render: (_: string, record: Machine) => {
        const reportId = record.reportedId ?? "";
        const isSelected = selectedReportIds.includes(reportId);
        const isDisabled =
          maxBulkPdfSelection == null ||
          isFuelMaintenanceReport(record) ||
          (!isSelected && selectedReportIds.length >= maxBulkPdfSelection);

        return (
          <Checkbox
            checked={isSelected}
            disabled={isDisabled}
            onChange={() => handleReportCheckboxChange(record)}
            onClick={(event) => event.stopPropagation()}
            sx={{
              color: "#333",
              "&.Mui-checked": {
                color: "#140a9a",
              },
            }}
          />
        );
      },
    },
    {
      key: "reportedAt",
      title: "tableMachineDetail.date",
      dataIndex: "reportedAt",
      align: "left",
      sorted: true,
      style: {
        minWidth: 150,
        fontSize: 14,
      },
      render: (value: any) => {
        return format(new Date(value), userTimeFormat1);
      },
      isLabel: true,
    },
    {
      key: "reportType",
      title: "subType",
      dataIndex: "reportType",
      align: "left",
      sorted: true,
      forceRender: true,
      style: {
        minWidth: 258,
      },
      render: (_: string, record: any) => (
        <div className="flex align-items-center justify-content-start">
          <div className="item-name ml-5 fs-14 word-break txt-pre-wrap">
            {record?.reportTypeMessage}
          </div>
          {!!record.reportSubType && (
            <span
              className={`item-label ml-8 txt-nowrap ${
                record?.reportSubType === ReportType.INSPECTION
                  ? "label-inquire"
                  : "label-report"
              }`}
            >
              {record?.reportSubType === ReportType.INSPECTION
                ? t("labelInspection")
                : t("groupDetailTab.reportTab")}
            </span>
          )}
        </div>
      ),
      isLabel: true,
    },

    {
      key: "reportItem",
      title: "tableMachineDetail.item",
      dataIndex: "reportItem",
      align: "left",
      sorted: true,
      style: {
        minWidth: isMobile() ? 220 : 252,
        fontSize: 14,
      },
      render: (value: string, record: any) => {
        const safeValue = value || "";

        const htmlValue = safeValue.replace(
          /®/g,
          '<span style="font-size:0.65em;position:relative;top:-0.5em;margin-left:1px;">®</span>',
        );

        return (
          <span
            className="txt-link"
            onClick={(e) => handleClick(e, record)}
            title={safeValue}
            style={{
              display: "inline-block",
              fontSize: 14,
              fontFamily: "inherit",
              maxWidth: "300px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              verticalAlign: "bottom",
              textDecorationSkipInk: "auto",
            }}
            dangerouslySetInnerHTML={{ __html: htmlValue }}
          />
        );
      },
      className: "txt-pre-wrap",
    },

    {
      key: "reportResponseStatus",
      title: "dialogReport.status",
      dataIndex: "reportResponseStatus",
      align: "left",
      sorted: true,
      style: {
        paddingLeft: 12,
        paddingRight: 9,
        minWidth: 130,
      },
      render: (value: ResponseStatus) =>
        !!value && (
          <span
            className={`label-status fs-14 ${
              value === ResponseStatus.RESOLVED
                ? "label-status-active"
                : "label-status-disable"
            } flex align-items-center justify-content-start`}
          >
            <span className="status" />
            <span className="label">
              {value === ResponseStatus.RESOLVED ? t("resolved") : t("open")}
            </span>
          </span>
        ),
      isLabel: true,
    },

    {
      key: "serviceMeter",
      title: "tableMachineDetail.serviceMeter",
      dataIndex: "serviceMeter",
      align: "right",
      style: {
        minWidth: 101,
        paddingLeft: 0,
        paddingRight: 0,
        fontSize: 14,
      },
      tableHeadingClassName: "txt-pre nowrap-heading",
      forceRender: true,
      isLabel: true,
      sorted: true,
      render: (value: number) =>
        value !== null && formatNumberWithCommas(value),
    },
    {
      key: "userPictureUrl",
      title: "",
      dataIndex: "userPictureUrl",
      align: "center",
      sorted: true,
      forceRender: true,
      render: (_: string, record: any) => (
        <div className="flex-centered justify-content-start">
          {!!record?.userPictureUrl ? (
            record?.userPictureUrl === INVALID_URL ? (
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <IconInvalidUrl />
              </Box>
            ) : (
              <Avatar
                sx={{
                  width: 24,
                  height: 24,
                }}
                alt="avatar-reporter"
                src={record?.userPictureUrl}
                imgProps={{
                  onError: () => {
                    const newRows = dataSource?.map((item) => {
                      if (item.id === record.id) {
                        item.userPictureUrl = INVALID_URL;
                      }
                      return item;
                    });
                    setDataSource(newRows);
                  },
                }}
              />
            )
          ) : (
            <Box
              sx={{
                width: 24,
                height: 24,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <IconUser />
            </Box>
          )}
        </div>
      ),
      className: "pr-4",
      tableHeadingClassName: "d-none-imp",
      style: {
        minWidth: 40,
      },
    },
    {
      key: "reporterName",
      title: "tableMachineDetail.reporter",
      dataIndex: "reporterName",
      align: "left",
      sorted: true,
      style: {
        minWidth: 100,
        fontSize: 14,
      },
      tableHeadingColspan: 2,
      className: "pl-0 txt-pre-wrap",
    },
    {
      key: "lat",
      title: "tableMachineDetail.location",
      dataIndex: "lat",
      style: {
        minWidth: 100,
      },
      align: "center",
      render: (_: string, record: any) =>
        record?.lat &&
        record?.lng && (
          <span
            className="flex-centered pointer"
            onClick={() => handleClickLocation(record)}
          >
            <IconLocationMachine />
          </span>
        ),
      isLabel: true,
    },
  ];

  const visibleColumns = isBulkPdfSelectionMode
    ? columns
    : columns.filter((column) => column.key !== "selection");

  const [machineReportId, setMachineReportId] = useState<string>("");
  const [openFormReport, setOpenFormReport] = useState<string>(INSPECTION);
  const [clickedReportVersion, setClickedReportVersion] = useState<
    string | null
  >(null);
  const [dialogKey, setDialogKey] = useState<number>(0);

  const handleClick = (_: React.MouseEvent<HTMLElement>, target: any) => {
    setMachineReportId(target.reportedId);
    setOpenFormReport(target.reportType);
    setClickedReportVersion(target.reportVersion ?? null);
    setDialogKey((prev) => prev + 1);
  };

  const handleDialogClose = () => {
    setMachineReportId("");
    setOpenFormReport(INSPECTION);
    setClickedReportVersion(null);

    setTimeout(() => {
      document.body.style.overflow = "auto";
      document.body.style.position = "static";
    }, 0);
  };

  const convertDataRow = (data: Machine[], localeCode: string) => {
    return data.map((row: any) => ({
      ...row,
      id: row?.reportedId,
      reporterName:
        localeCode === "ja"
          ? `${row?.surname} ${row?.givenName}`
          : `${row?.givenName} ${row?.surname}`,
    }));
  };

  const handleChangePage = (page: number) => {
    setPageInfo({
      ...pageInfo,
      page,
    });
  };

  const handleSort = (orderBy: string | undefined, property: string) => {
    setField(property);
    setOrder(orderBy);
  };

  const handleClickfilterCondition = (
    _: React.MouseEvent<HTMLElement>,
    filterCondition: ReportTypeDialog | "",
  ) => {
    setPageInfo({ ...pageInfo, page: CURRENT_PAGE });
    setFilterCondition(filterCondition);
  };
  const initScrollToTop = useCallback(() => {
    if (isInit) {
      scrollToTop();
      setIsInit(false);
    }
  }, [isInit]);

  const handleChangeStartDate = (newValue: Date) => {
    clearSearch();
    resetData();
    clearSelectedReports();
    setValueStartDate(newValue);
    if (valueEndDate < newValue) {
      setValueEndDate(newValue);
    }
  };

  const handleChangeEndDate = (newValue: Date) => {
    if (newValue && newValue < valueStartDate) return;
    clearSearch();
    resetData();
    clearSelectedReports();
    setValueEndDate(newValue);
  };

  const handleChangeStartExcelDate = (newValue: Date) => {
    clearSearch();
    resetData();
    setValueStartExcelDate(newValue);
  };

  const handleChangeEndExcelDate = (newValue: Date) => {
    if (newValue && newValue < valueStartExcelDate) return;
    clearSearch();
    resetData();
    setValueEndExcelDate(newValue);
  };

  useEffect(() => {
    setValueStartExcelDate(valueStartDate);
    setValueEndExcelDate(valueEndDate);
  }, [valueStartDate, valueEndDate]);

  const resetData = () => {
    setFilterCondition("");
    setPageInfo({ ...pageInfo, page: CURRENT_PAGE });
  };
  const [searchTextClear, setSearchTextClear] = useState("");
  const [searchTextReport, setSearchTextReport] = useState("");
  const isInitialFilterRender = React.useRef(true);

  useEffect(() => {
    if (isInitialFilterRender.current) {
      isInitialFilterRender.current = false;
      return;
    }

    if (isBulkPdfSelectionMode) {
      return;
    }

    clearSelectedReports();
  }, [filterCondition, clearSelectedReports, isBulkPdfSelectionMode]);

  function getCurrentTimezoneOffset(): string {
    const offsetMinutes = new Date().getTimezoneOffset();
    const absoluteMinutes = Math.abs(offsetMinutes);
    const sign = offsetMinutes <= 0 ? "+" : "-";
    const hours = String(Math.floor(absoluteMinutes / 60)).padStart(2, "0");
    const minutes = String(absoluteMinutes % 60).padStart(2, "0");

    return `${sign}${hours}:${minutes}`;
  }

  useEffect(() => {
    !!groupId &&
      !!machineId &&
      getMachineReportInfo(
        groupId,
        machineId,
        format(valueStartDate, DATE_TIME_FORMAT_2),
        format(valueEndDate, DATE_TIME_FORMAT_2),
        getCurrentUtc(),
        searchTextReport,
        getCurrentTimezoneOffset(),
      ).then((db) => {
        setLatestServiceMeter(
          db?.latestServiceMeter === null
            ? "-"
            : formatNumberWithCommas(db?.latestServiceMeter),
        );
        setLatestServiceMeterUpdatedAt(db?.latestServiceMeterUpdatedAt);
        const newDataMachineSummaryTabs = dataCountSummaryFilter.map((tab) => {
          tab.count = db[tab.key as keyof MachineSummaryType];
          return tab;
        });
        setDataCountSummaryTabList(newDataMachineSummaryTabs);
      });
  }, [
    groupId,
    machineId,
    valueStartDate,
    valueEndDate,
    filterCondition,
    isUpdatedMachineReportResponses,
    searchTextReport,
  ]);

  const [, setMachineInfo] = useState<MachineInfoType>();

  useEffect(() => {
    const getMachineDetails = async () => {
      if (!groupId || !machineId) return;

      try {
        const machineInfo = await getMachineInfo(groupId, machineId);
        setMachineInfo({
          ...machineInfo,
          machineManufacturerName: machineInfo.isOtherMachineManufacturer
            ? machineInfo.customMachineManufacturerName ||
              machineInfo.machineManufacturerName
            : machineInfo.machineManufacturerName,
        });
      } catch (error) {
        console.error("Failed to refresh machine data:", error);
      }
    };

    getMachineDetails();
  }, [groupId, machineId]);

  const handleSearch = () => {
    setPageInfo((prev) => ({
      ...prev,
      limit: LIMIT_PAGE,
      page: 1,
    }));
    setSearchTextReport(searchTextClear);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && searchTextClear.trim()) {
      handleSearch();
    }
  };

  const clearSearch = () => {
    setSearchTextClear("");
    setSearchTextReport("");
    setPageInfo((prev) => ({ ...prev, page: 1 }));
  };

  useEffect(() => {
    if (
      groupId &&
      machineId &&
      !!isRequestedMachineInfo &&
      !!isUpdatedMachineReportResponses
    ) {
      setIsLoading(true);
      getMachineSummary(groupId, machineId, {
        limit: LIMIT_PAGE,
        page: pageInfo?.page,
        ...(!!filterCondition && { filter: filterCondition }),
        ...(!!searchTextReport && { search: searchTextReport }),
        startDate: format(valueStartDate, DATE_TIME_FORMAT_2),
        endDate: format(valueEndDate, DATE_TIME_FORMAT_2),
        ...(field && order && { orderBys: [{ field: field, order: order }] }),
        utc: getCurrentUtc(),
        timezoneOffset: getCurrentTimezoneOffset(),
      })
        .then(
          (db: {
            data: Machine[];
            meta: {
              pageInfo: pageInfo;
              screenPermission: screenPermission;
            };
          }) => {
            setDataSource(convertDataRow(db?.data, localeCode));
            setPageInfo(db?.meta.pageInfo);
            if (db?.meta?.screenPermission) {
              dispatch(saveScreenPermission(db.meta.screenPermission));
            }
            if (!db?.data.length) {
              initScrollToTop();
              setIsLoading(false);
            }
          },
        )
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [
    groupId,
    machineId,
    pageInfo?.page,
    field,
    order,
    valueStartDate,
    valueEndDate,
    filterCondition,
    searchTextReport,
    isRequestedMachineInfo,
    isUpdatedMachineReportResponses,
  ]);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const getBulkExportFileName = () => {
    const prefix = sanitizeBulkExportFileNameDateTime(
      t("bulkPdfExportFileNamePrefix"),
    );
    const dateStamp = sanitizeBulkExportFileNameDateTime(
      format(new Date(), userTimeFormat || "yyyy/MM/dd"),
    );

    return `${prefix}_${dateStamp}.pdf`;
  };

  const handleInputPdfHeaderChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    const isInvalid =
      !REGEX.commonNameFieldCharacterMuiltiLineRegex.test(value) ||
      !REGEX.emojiRegex.test(value);

    setPdfHeaderRemarks(value);
    setPdfHeaderRemarksError(isInvalid);
  };

  const handleInputPdfFooterChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    const isInvalid =
      !REGEX.commonNameFieldCharacterMuiltiLineRegex.test(value) ||
      !REGEX.emojiRegex.test(value);

    setPdfFooterRemarks(value);
    setPdfFooterRemarksError(isInvalid);
  };

  const handleBulkPdfExportClick = async () => {
    if (
      selectedReportIds.length === 0 ||
      isCheckingBulkPdfMediaCount ||
      isBulkPdfExporting
    ) {
      return;
    }

    setIsCheckingBulkPdfMediaCount(true);

    try {
      const response = await getReportsMediaCount(selectedReportIds);
      const mediaCount = response?.data?.data?.mediaCount;
      const maxMediaCount = response?.data?.data?.maxReportAttachmentPrintCount;

      if (typeof mediaCount !== "number" || typeof maxMediaCount !== "number") {
        throw new Error("Invalid media-count response.");
      }

      const isMediaLimitExceeded = mediaCount > maxMediaCount;

      setIsBulkPdfMediaLimitExceeded(isMediaLimitExceeded);
      setBulkPdfMediaCountInfo({ mediaCount, maxMediaCount });

      if (isMediaLimitExceeded) {
        setOpenBulkPdfMediaLimitDialog(true);
        return;
      }

      setOpenBulkPdfConfirmDialog(true);
    } catch (error) {
      console.error("Error fetching report media count:", error);
      store.dispatch(setShowAlert(t("exportreportfailed")));
    } finally {
      setIsCheckingBulkPdfMediaCount(false);
    }
  };

  const handleBulkPdfExport = async () => {
    if (!groupId || !machineId || selectedReportIds.length === 0) {
      return;
    }

    if (pdfHeaderRemarksError || pdfFooterRemarksError) {
      return;
    }

    const reports = selectedReportIds
      .map((reportId) => {
        const meta = selectedReportMetaMap[reportId];

        return {
          reportId,
          type: meta?.type ?? "",
          reportedAt: meta?.reportedAt ?? "",
        };
      })
      .filter((report) => report.reportId && report.type && report.reportedAt)
      .sort(
        (a, b) =>
          new Date(b.reportedAt).getTime() - new Date(a.reportedAt).getTime(),
      );

    if (reports.length === 0) {
      store.dispatch(setShowAlert(t("exportreportfailed")));
      return;
    }

    setIsBulkPdfExporting(true);

    try {
      const meta = await getBulkReportsPdf(
        groupId,
        machineId,
        reports,
        timeZone,
        false,
        getBulkExportFileName(),
        !isBulkPdfMediaLimitExceeded,
        pdfHeaderRemarks,
        pdfFooterRemarks,
      );

      if (meta?.screenPermission) {
        dispatch(saveScreenPermission(meta.screenPermission));
      }

      store.dispatch(setShowAlert(t("exportreportssuccessfully")));
      resetBulkPdfExportState();
      setIsBulkPdfSelectionMode(false);
      setOpenBulkPdfConfirmDialog(false);
      setOpenBulkPdfMediaLimitDialog(false);
      setOpenBulkPdfRemarksDialog(false);
    } catch {
      store.dispatch(setShowAlert(t("exportreportfailed")));
    } finally {
      setIsBulkPdfExporting(false);
    }
  };

  const handleExportReportsClick = async () => {
    if (!machineId) {
      console.error("Machine ID is missing.");
      return;
    }

    const formattedStart = format(valueStartExcelDate, "yyyy-MM-dd");
    const formattedEnd = format(valueEndExcelDate, "yyyy-MM-dd");

    try {
      const response = await groupsMachinesReportsCSV(
        [machineId],
        formattedStart,
        formattedEnd,
        timeZone,
      );

      const data = response?.data?.data;

      if (!data || !Array.isArray(data) || data.length === 0) {
        store.dispatch(setShowAlert(t("exportreportfailedNoData")));
        return;
      }

      const headers = [
        t("groupName"),
        t("machineInfo.maker"),
        t("machineInfo.customerMachineNumber"),
        t("modelType"),
        t("machineInfo.serialNo"),
        t("tableMachineDetail.date"),
        t("subType"),
        t("tableMachineDetail.item"),
        t("dialogReport.comment"),
        t("fuelAmount"),
        t("adBlueAmount"),
        t("serviceMeterOrSMR"),
        t("tableMachineDetail.reporter"),
        t("latitude"),
        t("longitude"),
      ];

      const rows = data.map((row) => [
        row.groupName ?? "",
        row.machineManufacturerName ?? "",
        row.customerMachineName ?? "",
        row.modelType ?? "",
        row.serialNumber ?? "",
        format(new Date(row.dateTime), userTimeFormat1) ?? "",
        row.reportTypeMessage ?? "",
        row.item ?? "",
        row.comment ?? "",
        row.fuelAmount ?? "",
        row.adblueAmount ?? "",
        row.serviceMeter ?? "",
        row.reporterName ?? "",
        row.lat ?? "",
        row.lng ?? "",
      ]);

      const csvContent = [headers, ...rows]
        .map((e) =>
          e.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
        )
        .join("\n");

      const BOM = "\uFEFF";
      const blob = new Blob([BOM + csvContent], {
        type: "text/csv;charset=utf-8;",
      });

      const dateStart = format(valueStartExcelDate, userTimeFormat).replace(
        /[-/]/g,
        "",
      );
      const dateEnd = format(valueEndExcelDate, userTimeFormat).replace(
        /[-/]/g,
        "",
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      store.dispatch(setShowAlert(t("exportreportssuccessfully")));
      link.download = `${data[0].customerMachineName}_${dateStart}-${dateEnd}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      setExportReports(false);
    } catch (error) {
      console.error("Export failed:", error);
      store.dispatch(setShowAlert(t("exportreportfailed")));
    }
  };

  useEffect(() => {
    return () => {
      document.body.style.overflow = "auto";
      document.body.style.position = "static";
    };
  }, []);

  useEffect(() => {
    if (!machineReportId) {
      setTimeout(() => {
        document.body.style.overflow = "auto";
        document.body.style.position = "static";
      }, 100);
    }
  }, [machineReportId]);

  return (
    <div
      className="pb-40"
      style={isBulkPdfSelectionMode ? { paddingBottom: "120px" } : undefined}
    >
      <Box
        className="wrap-filter-date mt-24"
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "flex-start" },
          justifyContent: "space-between",
          gap: { xs: 2, md: 0 },
          px: { xs: 2, sm: 3 },
          boxSizing: "border-box",
        }}
      >
        <Box
          className="date-range sp-p24"
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "flex-start",
            width: "100%",
            gap: { xs: 1, sm: 0 },
            p: { xs: 0, sm: undefined },
          }}
        >
          <DatePickerInput
            label={t("report.labelStartDate")}
            value={valueStartDate}
            onChange={(newValue) => {
              handleChangeStartDate(newValue);
            }}
            onAccept={(newValue) => {
              handleChangeStartDate(newValue);
            }}
            maxDate={valueEndDate}
          />
          <Box
            component="h3"
            className="wrap-end-date m-0 fs-24 txt-black-1 fw-700 lh-24"
            sx={{
              mt: { xs: 0.5, sm: 0 },
              mb: 0,
            }}
          >
            <span className="symbol">~</span>
          </Box>
          <DatePickerInput
            label={t("report.labelEndDate")}
            value={valueEndDate}
            onChange={(newValue) => {
              handleChangeEndDate(newValue);
            }}
            onAccept={(newValue) => {
              handleChangeEndDate(newValue);
            }}
            minDate={valueStartDate}
          />
        </Box>
        <Box
          className="wrap-right sp-p24"
          sx={{
            display: "flex",
            alignItems: { xs: "center", md: "flex-start" },
            justifyContent: { xs: "space-between", md: "flex-start" },
            width: { xs: "100%", md: "auto" },
            ml: { xs: 0, md: 3 },
            pr: { xs: 0, md: 3 },
            p: { xs: 0, sm: undefined },
          }}
        >
          <div className="wrap-service-meter">
            <div className="text-right ml-40 content">
              <p
                className="txt-gray-1 fs-12 fw-600 m-0"
                style={{ whiteSpace: "nowrap" }}
              >
                {t("report.labelSeviceMeter")}
              </p>

              <p
                className="fs-18 fw-700 lh-24 txt-black-1 m-0 value-pc word-break"
                dir="ltr"
              >
                {latestServiceMeter} h
              </p>
              {!!latestServiceMeterUpdatedAt && (
                <p className="txt-gray-3 fs-12 fw-500 m-0">
                  {format(latestServiceMeterUpdatedAt, userTimeFormat1)}
                </p>
              )}
            </div>

            <p
              className="fs-18 fw-700 lh-24 txt-black-1 m-0 value-tl word-break"
              dir="ltr"
            >
              {latestServiceMeter} h
            </p>
          </div>
        </Box>
      </Box>

      <Box
        className="toggle-btn-group sp-p24"
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "nowrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          marginTop: "24px",
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          px: { xs: 2, sm: 3 },
          "@media (max-width: 1480px)": {
            flexDirection: "column",
            alignItems: "stretch",
            flexWrap: "wrap",
          },
        }}
      >
        <Box
          sx={{
            flex: "1 1 0%",
            minWidth: 0,
            maxWidth: "100%",
            overflow: "hidden",
            "@media (max-width: 1480px)": {
              flex: "1 1 100%",
              width: "100%",
            },
          }}
        >
          <ToggleButtonGroup
            sx={{
              overflowX: "auto",
              maxWidth: "100%",
              width: "100%",
              minWidth: 0,
              display: "flex !important",
              flexWrap: "nowrap",
              boxSizing: "border-box",
              WebkitOverflowScrolling: "touch",
              pb: { xs: 0.5, sm: 0 },
              "& .MuiToggleButton-root": {
                flexShrink: 0,
                fontSize: { xs: "12px", sm: "14px" },
                px: { xs: 1.5, sm: 2 },
                py: { xs: 0.75, sm: 1 },
              },
              "&::-webkit-scrollbar": { height: 6 },
            }}
            exclusive
            value={filterCondition}
          >
            {dataCountSummaryTabList.map((tab, index) => (
              <ToggleButton
                key={index}
                value={tab.filterCondition}
                className="flex-centered filter-tab"
                onClick={handleClickfilterCondition}
              >
                {tab.icon && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      marginInlineEnd: "5px",
                    }}
                  >
                    {tab.icon}
                  </Box>
                )}
                {t(tab.label)}
                <span style={{ marginInlineStart: "8px" }}>({tab.count})</span>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <Box
          sx={{
            display: "flex",
            flex: "0 0 auto",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: { xs: 1, sm: 1.5 },
            flexWrap: "wrap",
            justifyContent: { xs: "stretch", sm: "flex-end" },
            width: "auto",
            maxWidth: "100%",
            ml: "auto",
            "@media (max-width: 1480px)": {
              width: "100%",
              ml: 0,
              justifyContent: "flex-end",
            },
          }}
        >
          {canCreateInspectionAndReport && (
            <>
              <Button
                className="btn btn-primary"
                sx={{
                  whiteSpace: { xs: "normal", sm: "nowrap" },
                  width: { xs: "100%", sm: "auto" },
                  flex: { xs: "1 1 auto", sm: "0 1 auto" },
                  minWidth: { sm: "min-content" },
                  maxWidth: "100%",
                  py: { xs: 1, sm: undefined },
                  "& .MuiButton-startIcon": {
                    marginInlineEnd: "0px",
                  },
                }}
                startIcon={<Add sx={{ fontSize: "1.35rem" }} />}
                disabled={isBulkPdfSelectionMode}
                onClick={async (event) => {
                  const anchor = event.currentTarget;
                  await prefetchPeriodicScheduleAvailability();
                  setCreateReportMenuAnchor(anchor);
                }}
              >
                {t("report.createReports")}
              </Button>
              <Menu
                anchorEl={createReportMenuAnchor}
                open={isCreateReportMenuOpen}
                onClose={closeCreateReportMenus}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
              >
                {createReportMenuOptions.map((option) => (
                  <MenuItem
                    key={option.filterCondition}
                    onClick={() => {
                      closeCreateReportMenus();
                      if (option.filterCondition === INSPECTION) {
                        setOpenCreateInspection(true);
                      } else if (option.filterCondition === INCIDENT_REPORTS) {
                        setOpenCreateAbnormality(true);
                      } else if (
                        option.filterCondition === FUEL_MAINTENANCE_REPORTS
                      ) {
                        setOpenCreateRefuel(true);
                      }
                    }}
                    sx={{ gap: "8px", minWidth: 200 }}
                  >
                    {option.icon && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {option.icon}
                      </Box>
                    )}
                    {t(option.label)}
                  </MenuItem>
                ))}
                <MenuItem
                  onMouseEnter={(event) =>
                    setMaintenanceSubMenuAnchor(event.currentTarget)
                  }
                  onClick={(event) =>
                    setMaintenanceSubMenuAnchor(event.currentTarget)
                  }
                  sx={{
                    gap: "8px",
                    minWidth: 200,
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "8px" }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <MaintenanceReportIcon />
                    </Box>
                    {t("maintenanceReport")}
                  </Box>
                  <ChevronRightIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                </MenuItem>
              </Menu>
              <Menu
                anchorEl={maintenanceSubMenuAnchor}
                open={isMaintenanceSubMenuOpen}
                onClose={() => setMaintenanceSubMenuAnchor(null)}
                anchorOrigin={{
                  vertical: -MENU_ITEM_HEIGHT_PX,
                  horizontal: "right",
                }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                slotProps={{
                  paper: { sx: { ml: "1px" } },
                }}
                MenuListProps={{
                  onMouseLeave: () => setMaintenanceSubMenuAnchor(null),
                }}
                disableAutoFocus
                disableEnforceFocus
              >
                {maintenanceSubMenuOptions.map((option) => (
                  <MenuItem
                    key={option.label}
                    onClick={() => {
                      closeCreateReportMenus();
                      setOpenCreateMaintenance(true);
                      setMaintenanceType(option.maintenanceType ?? "");
                    }}
                    sx={{ gap: "8px", minWidth: 200 }}
                  >
                    {option.icon && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {option.icon}
                      </Box>
                    )}
                    {t(option.label)}
                  </MenuItem>
                ))}
              </Menu>
              {/* Drafts button hidden — feature deferred to Phase 5 (draft sharing under consideration), implementation kept as-is. */}
              {false && maintenanceDraftsCount > 0 && (
                <Button
                  className="btn btn-no-border-dark-blue"
                  sx={{
                    whiteSpace: { xs: "normal", sm: "nowrap" },
                    width: { xs: "100%", sm: "auto" },
                    flex: { xs: "1 1 auto", sm: "0 1 auto" },
                    minWidth: { sm: "min-content" },
                    maxWidth: "100%",
                    py: { xs: 1, sm: undefined },
                  }}
                  onClick={() => setOpenMaintenanceDrafts(true)}
                >
                  {t("draftReports")} ({maintenanceDraftsCount})
                </Button>
              )}
            </>
          )}
          <Button
            className="btn btn-no-border-dark-blue"
            sx={{
              whiteSpace: { xs: "normal", sm: "nowrap" },
              width: { xs: "100%", sm: "auto" },
              flex: { xs: "1 1 auto", sm: "0 1 auto" },
              minWidth: { sm: "min-content" },
              maxWidth: "100%",
              py: { xs: 1, sm: undefined },
              "& .MuiButton-startIcon": {
                marginInlineEnd: "4px",
              },
              "&.Mui-disabled": {
                color: "#a6a9ab",
                border: "1px solid #a6a9ab",
                backgroundColor: "unset",
              },
            }}
            startIcon={<PictureAsPdfOutlined sx={{ fontSize: "1.25rem" }} />}
            disabled={
              isBulkPdfSelectionMode ||
              isCheckingBulkPdfMediaCount ||
              isBulkPdfExporting
            }
            onClick={handleEnterBulkPdfSelectionMode}
          >
            {t("bulkPdfExport")}
          </Button>
          <Button
            className="btn btn-no-border btn-csv-export"
            sx={{
              whiteSpace: { xs: "normal", sm: "nowrap" },
              width: { xs: "100%", sm: "auto" },
              flex: { xs: "1 1 auto", sm: "0 1 auto" },
              minWidth: { sm: "min-content" },
              maxWidth: "100%",
              py: { xs: 1, sm: undefined },
            }}
            disabled={isBulkPdfSelectionMode}
            onClick={() => {
              setExportReports(true);
            }}
          >
            {t("excel")}
          </Button>
        </Box>
      </Box>

      <Box
        className="input-field"
        sx={{
          display: "flex",
          justifyContent: { xs: "stretch", sm: "flex-end" },
          alignItems: "center",
          marginBottom: "-10px",
          marginTop: "20px",
          paddingRight: 0,
          paddingX: { xs: 2, sm: 3 },
          position: "relative",
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ position: "relative", width: { xs: "100%", sm: "auto" } }}>
          <InputText
            type="text"
            value={searchTextClear}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value;

              setSearchTextClear(value);
              if (value.trim() === "") {
                setSearchTextClear("");
                setSearchTextReport("");
                setPageInfo((prev) => ({ ...prev, page: 1 }));
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder={t("Customermachinenumber")}
            className="custom-input"
            style={{
              width: "100%",
              maxWidth: "277px",
              paddingLeft: "1.0rem",
              paddingRight: "2rem",
              height: "50px",
              border: "1.5px solid #bcbcbc",
              borderRadius: "30px",
              boxSizing: "border-box",
            }}
          />
          {searchTextClear && (
            <button
              className="clear-button"
              onClick={clearSearch}
              aria-label="Clear search"
              type="button"
            >
              ×
            </button>
          )}
        </Box>
      </Box>

      <Box className="data-tabs" sx={{ width: "100%", marginTop: "24px" }}>
        <DataTable
          rowKey="reportedId"
          columns={visibleColumns}
          dataSource={dataSource}
          textEmptyData="noReport"
          onRequestSort={handleSort}
          didScrollTop={() => {
            initScrollToTop();
            resetIsContinuousLoading();
          }}
          loading={isLoading}
        />
        <DataPagination
          isHidden={!dataSource?.length}
          currentPage={pageInfo?.page}
          limitPage={pageInfo?.limit}
          totalPage={pageInfo?.total}
          className="mt-24 sp-p24"
          handleChangePage={handleChangePage}
        />
      </Box>

      {dataCell && (
        <DataDialog
          title={t("tableMachineDetail.location")}
          open={open}
          setOpen={setOpen}
          className="dialog-location-report"
          disableBackdropClick={true}
        >
          <iframe
            title="map-location"
            className="map-iframe"
            loading="lazy"
            src={`https://maps.google.com/maps?&q="+${dataCell?.lat}, ${dataCell?.lng}"&output=embed`}
          />
          <span className="fs-14 txt-gray-1 label-dialog">
            {t("tableMachineDetail.date")}
          </span>
          <p className="fs-16 txt-gray mt-4">
            {dataCell?.firstReportedAt &&
              format(new Date(dataCell?.firstReportedAt), userTimeFormat1)}
          </p>
          <span className="fs-14 txt-gray-1 label-dialog">
            {t("tableMachineDetail.reporter")}
          </span>
          <div className="flex-centered justify-content-start mt-8">
            {dataCell?.userPictureUrl ? (
              dataCell.userPictureUrl === INVALID_URL ? (
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <IconInvalidUrl />
                </Box>
              ) : (
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                  }}
                  alt="avatar-reporter"
                  src={dataCell?.userPictureUrl}
                  imgProps={{
                    onError: () => {
                      setDataCell({
                        ...dataCell,
                        userPictureUrl: INVALID_URL,
                      });
                    },
                  }}
                />
              )
            ) : (
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <IconUser />
              </Box>
            )}

            <span className="fs-16 txt-gray ml-5 word-break txt-pre-wrap">
              {localeCode === "ja"
                ? `${dataCell?.surname} ${dataCell?.givenName}`
                : `${dataCell?.givenName} ${dataCell?.surname}`}
            </span>
          </div>
          <div className="flex justify-content-end">
            <Button
              onClick={() => setOpen(false)}
              className="btn btn-no-border"
            >
              {t("dialogRefuel.close")}
            </Button>
          </div>
        </DataDialog>
      )}

      <DataDialog
        open={openExportReports}
        setOpen={setExportReports}
        disableBackdropClick={true}
        title={
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span>{t("exportreports")}</span>
            <span
              className="fs-16 txt-gray-3"
              style={{ color: "#6B7280", marginTop: "-4px" }}
            ></span>
          </div>
        }
      >
        <div
          className="date-range-responsive"
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            gap: "16px",
            marginTop: "20px",
            flexWrap: "nowrap",
          }}
        >
          <div className="datepicker-wrapper">
            <DatePickerExcelInput
              label={t("report.labelStartDate")}
              value={valueStartExcelDate}
              onChange={handleChangeStartExcelDate}
              onAccept={handleChangeStartExcelDate}
            />
          </div>

          <div
            className="tilde-symbol"
            style={{
              fontSize: "20px",
              fontWeight: 600,
              alignSelf: "center",
              paddingBottom: "6px",
            }}
          >
            ~
          </div>

          <div className="datepicker-wrapper">
            <DatePickerExcelInput
              label={t("report.labelEndDate")}
              value={valueEndExcelDate}
              onChange={handleChangeEndExcelDate}
              onAccept={handleChangeEndExcelDate}
              minDate={valueStartExcelDate}
            />
          </div>
        </div>

        <div
          className="flex justify-end btn-group-footer mt-8"
          style={{
            backgroundColor: "#fff",
            paddingTop: "20px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            position: "sticky",
            bottom: 0,
            zIndex: 10,
          }}
        >
          <Button
            style={{ width: "auto", marginTop: "4px" }}
            className="btn btn-no-border"
            onClick={() => {
              setExportReports(false);
            }}
          >
            {t("dialogReport.cancel")}
          </Button>

          <Button
            className="btn btn-primary"
            style={{ width: "auto", marginTop: "4px" }}
            disabled={isExportDisabled}
            onClick={handleExportReportsClick}
          >
            {t("export")}
          </Button>
        </div>
      </DataDialog>

      <DataDialog
        open={openBulkPdfMediaLimitDialog}
        disableBackdropClick={true}
        setOpen={() => setOpenBulkPdfMediaLimitDialog(false)}
        className="dialog-container"
      >
        <p className="fs-16" style={{ whiteSpace: "pre-line" }}>
          {t("bulkPdfMediaLimitWarning", {
            count: bulkPdfMediaCountInfo?.mediaCount ?? 0,
            max: bulkPdfMediaCountInfo?.maxMediaCount ?? 0,
          })}
        </p>

        <div
          className="flex justify-content-end group-btn-footer"
          style={{ gap: "12px" }}
        >
          <Button
            className="btn btn-no-border"
            onClick={() => {
              setOpenBulkPdfMediaLimitDialog(false);
              resetBulkPdfExportState();
            }}
          >
            {t("dialogReport.cancel")}
          </Button>

          <Button
            className="btn btn-primary ml-8"
            onClick={() => {
              setOpenBulkPdfMediaLimitDialog(false);
              setOpenBulkPdfRemarksDialog(true);
            }}
          >
            {t("ok")}
          </Button>
        </div>
      </DataDialog>

      <DataDialog
        open={openBulkPdfConfirmDialog}
        disableBackdropClick={true}
        setOpen={() => setOpenBulkPdfConfirmDialog(false)}
        className="dialog-container"
      >
        <p className="fs-16" style={{ whiteSpace: "pre-line" }}>
          {t("pdfExpireWarning")}
        </p>

        <div
          className="flex justify-content-end group-btn-footer"
          style={{ gap: "12px" }}
        >
          <Button
            className="btn btn-no-border"
            onClick={() => {
              setOpenBulkPdfConfirmDialog(false);
              resetBulkPdfExportState();
            }}
          >
            {t("dialogReport.cancel")}
          </Button>

          <Button
            className="btn btn-primary ml-8"
            onClick={() => {
              setOpenBulkPdfConfirmDialog(false);
              setOpenBulkPdfRemarksDialog(true);
            }}
          >
            {t("ok")}
          </Button>
        </div>
      </DataDialog>

      <DataDialog
        open={openBulkPdfRemarksDialog}
        setOpen={handleBulkPdfExportDialogClose}
        disableBackdropClick={true}
        title={
          <p
            style={{
              fontWeight: "bold",
              fontSize: "24px",
              marginBottom: "0px",
              marginTop: "0px",
              color: "black",
            }}
          >
            {t("PDFRemarks")}
          </p>
        }
        className="dialog-container"
      >
        <p className="txt-gray-1 fs-16">{t("pfdComments")}</p>
        <div className="wrapper-text-field mt-8">
          <TextField
            multiline
            rows={3}
            className="input-field"
            label={t("HeaderRemarks")}
            value={pdfHeaderRemarks}
            fullWidth
            inputProps={{ maxLength: 1000 }}
            onChange={handleInputPdfHeaderChange}
            error={pdfHeaderRemarksError}
            helperText={pdfHeaderRemarksError ? t("invalidCharacters") : ""}
            InputProps={{
              endAdornment: (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  {pdfHeaderRemarksError && <IconInputError />}
                </div>
              ),
            }}
          />
        </div>

        <div className="wrapper-text-field mt-8">
          <TextField
            multiline
            rows={3}
            className="input-field"
            label={t("FooterRemarks")}
            value={pdfFooterRemarks}
            fullWidth
            inputProps={{ maxLength: 1000 }}
            onChange={handleInputPdfFooterChange}
            error={pdfFooterRemarksError}
            helperText={pdfFooterRemarksError ? t("invalidCharacters") : ""}
            InputProps={{
              endAdornment: (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  {pdfFooterRemarksError && <IconInputError />}
                </div>
              ),
            }}
          />
        </div>

        <div
          className="flex justify-content-end group-btn-footer"
          style={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "white",
            paddingTop: "16px",
            gap: "8px",
            zIndex: 1000,
          }}
        >
          <Button
            className="btn btn-no-border"
            onClick={handleBulkPdfExportDialogClose}
            disabled={isBulkPdfExporting}
          >
            {t("dialogReport.cancel")}
          </Button>

          <Button
            className="btn btn-primary"
            onClick={handleBulkPdfExport}
            disabled={
              isBulkPdfExporting ||
              pdfHeaderRemarksError ||
              pdfFooterRemarksError
            }
          >
            {t("pdfOutput")}
          </Button>
        </div>
      </DataDialog>

      {/* BulkPdfExportDialog kept in project for future use
      <BulkPdfExportDialog
        open={openBulkPdfExport}
        onClose={handleBulkPdfExportDialogClose}
        selectedReportIds={selectedReportIds}
        selectedReportTypes={selectedReportTypes}
        userTimeFormat={userTimeFormat}
        isExporting={isBulkPdfExporting}
        onExport={handleBulkPdfExport}
      />
      */}

      {machineReportId && openFormReport !== ReportType.INSPECTION && (
        <AllReportsDataDialog
          key={dialogKey}
          id={groupId}
          machineId={machineId || ""}
          machineReportId={machineReportId}
          reportType={openFormReport}
          reportVersion={clickedReportVersion}
          onClose={handleDialogClose}
        />
      )}

      {machineReportId && openFormReport === ReportType.INSPECTION && (
        <InspectionReportItemsDialog
          key={dialogKey}
          id={groupId}
          machineId={machineId || ""}
          machineReportId={machineReportId}
          onClose={() => {
            setMachineReportId("");
          }}
        />
      )}

      {isBulkPdfSelectionMode && (
        <BulkPdfSelectionBar
          selectedCount={selectedReportIds.length}
          maxCount={maxBulkPdfSelection}
          isDownloadDisabled={
            selectedReportIds.length === 0 ||
            isCheckingBulkPdfMediaCount ||
            isBulkPdfExporting
          }
          onClearAll={handleClearAllSelectedReports}
          onCancel={handleCancelBulkPdfSelection}
          onDownload={handleBulkPdfExportClick}
        />
      )}

      <CreateInspections
        open={openCreateInspection}
        onClose={() => setOpenCreateInspection(false)}
        groupId={groupId || ""}
        machineId={machineId || ""}
      />

      <CreateAbnormality
        open={openCreateAbnormality}
        onClose={() => setOpenCreateAbnormality(false)}
        groupId={groupId || ""}
        machineId={machineId || ""}
      />

      <CreateRefuel
        open={openCreateRefuel}
        onClose={() => setOpenCreateRefuel(false)}
        groupId={groupId || ""}
        machineId={machineId || ""}
      />

      <CreateMaintenance
        open={openCreateMaintenance}
        onClose={() => setOpenCreateMaintenance(false)}
        groupId={groupId || ""}
        machineId={machineId || ""}
        maintenanceType={
          maintenanceType || MaintenanceReportType.GENERAL_MAINTENANCE
        }
        prefetchedPeriodicScheduleAvailable={periodicScheduleAvailable}
      />

      <MaintenanceDraftsListDialog
        open={openMaintenanceDrafts}
        onClose={() => setOpenMaintenanceDrafts(false)}
        groupId={groupId || ""}
        machineId={machineId || ""}
        onDraftPublished={fetchMaintenanceDraftsCount}
      />
    </div>
  );
}
